import React, { FC, ReactNode, useMemo } from 'react';

import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { BroadcastPanelCategory } from '../../enums';
import { BroadcastPanelData, Country, PanelBroadcast } from '../../types';

import BroadcastAreaRow from './components/RegionalNetworkRow/BroadcastAreaRow';
import Broadcast from './components/broadcast/Broadcast';

import { BroadcastsList, PanelBroadcastWrapper, PanelHeader } from './styles';

type BroadcastPanelProps = {
    panel: BroadcastPanelData;
    selectedCountry: Country;
};

const BroadcastPanel: FC<BroadcastPanelProps> = ({ panel, selectedCountry }) => {
    const panelBroadcasters = useMemo<ReactNode>(() => {
        const renderBroadcasters = (panel: BroadcastPanelData) => {
            // for regional broadcasts there are no "links", just a list of broadcasters for each team (if there are any)
            if (panel.categoryId === BroadcastPanelCategory.regional) {
                const { awayTeam, homeTeam } = panel;

                return (
                    <>
                        {!!homeTeam?.broadcasters?.length && <BroadcastAreaRow team={homeTeam} />}
                        {!!awayTeam?.broadcasters?.length && <BroadcastAreaRow team={awayTeam} />}
                    </>
                );
            }

            const broadcastersForCountry = panel.broadcasters?.filter?.(
                (broadcaster) => broadcaster.countryCode === selectedCountry.abbr
            ) as Array<PanelBroadcast>;

            return broadcastersForCountry?.map?.((broadcaster: PanelBroadcast) => (
                <Broadcast
                    key={`broadcaster-${broadcaster.id}`}
                    broadcast={broadcaster}
                    description={panel.description}
                    isOtherCountriesPanel={panel.categoryId === BroadcastPanelCategory.otherCountries}
                />
            ));
        };

        return renderBroadcasters(panel);
    }, [panel, selectedCountry.abbr]);

    if (panel.categoryId !== BroadcastPanelCategory.regional && !panel?.broadcasters?.length) {
        return null;
    }

    return (
        <PanelBroadcastWrapper>
            <BroadcastsList>
                <PanelHeader>{parseValueFromLocalizedAttribute(panel.title)}</PanelHeader>
                {panelBroadcasters}
            </BroadcastsList>
        </PanelBroadcastWrapper>
    );
};

export default BroadcastPanel;
